import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCog, faVolumeMute, faVolumeUp } from "@fortawesome/pro-light-svg-icons"
import { fetchFileVirtual } from "../../helpers/file"
import { ReactComponent as PlayIcon } from "../../assets/images/audio-player/play.svg"
import { ReactComponent as PauseIcon } from "../../assets/images/audio-player/pause.svg"
import { ReactComponent as RepeatIcon } from "../../assets/images/audio-player/repeat.svg"
import { ReactComponent as DownloadIcon } from "../../assets/images/audio-player/download.svg"
import styles from "./AudioPlayer.module.scss"
import cn from "classnames"
import Skeleton from "../Skeleton/Skeleton"
import { v4 as uuid } from "uuid"
import { dialogsTestIds } from "../../utility/tests/testId/dialogs"

export type AudioPlayerProps = {
    attrs: {
        href: string
        payload: {
            fileName: string
            fileType: string
        }
    }
    className?: string
    settingsClassName?: string
    primary?: boolean
    small?: boolean
}

const tNamespace = "knowledgeBase:article-content-editor"

const CURRENT_AUDIO_ID_KEY = "CurrentAudioId"

export const AudioPlayer = (props: AudioPlayerProps) => {
    const { t } = useTranslation()

    const [isPlaying, setPlaying] = useState(false)
    const [isMuted, setMuted] = useState(false)
    const [currentTimeStr, setCurrentTimeStr] = useState("00:00")
    const [totalTimeStr, setTotalTimeStr] = useState("00:00")
    const [isMoving, setMoving] = useState(false)
    const [isSettingsOpened, setSettingsOpened] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const audioPlayerId = useRef(uuid())

    const audioRef = useRef<HTMLAudioElement>(null)
    const lineRef = useRef<HTMLDivElement>(null)
    const progressRef = useRef<HTMLDivElement>(null)
    const thumbRef = useRef<HTMLDivElement>(null)

    const currentAudioId = window.localStorage.getItem(CURRENT_AUDIO_ID_KEY)
    const normalizedFileNameId = props.attrs.payload.fileName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    const audioItemId = normalizedFileNameId || "item"

    const handlePlay = () => {
        if (audioRef.current) {
            window.localStorage.setItem(CURRENT_AUDIO_ID_KEY, audioPlayerId.current)
            setPlaying(true)
            audioRef.current.play()
        }
    }

    const handleEnded = () => {
        setPlaying(false)
        window.localStorage.setItem(CURRENT_AUDIO_ID_KEY, "")
    }

    useEffect(() => {
        if (audioRef.current && currentAudioId !== audioPlayerId.current) {
            setPlaying(false)
            audioRef.current.pause()
        }
    }, [currentAudioId])

    useEffect(() => {
        const handleChangeCurrentAudioId = () => {
            if (audioRef.current && currentAudioId !== audioPlayerId.current) {
                setPlaying(false)
                audioRef.current.pause()
            }
        }

        window.addEventListener("storage", handleChangeCurrentAudioId)

        return () => {
            if (currentAudioId === audioPlayerId.current) {
                window.localStorage.setItem(CURRENT_AUDIO_ID_KEY, "")
            }

            window.removeEventListener("storage", handleChangeCurrentAudioId)
        }
    }, [])

    const handlePause = () => {
        if (audioRef.current) {
            setPlaying(false)
            audioRef.current.pause()
        }
    }

    const toggleMute = () => {
        if (audioRef.current) {
            if (isMuted) {
                setMuted(false)
                audioRef.current.volume = 1
            } else {
                setMuted(true)
                audioRef.current.volume = 0
            }
        }
    }

    const getTimeMinutesOrSecondsStr = (value: number, adder: string, length: number) => {
        return (new Array(length + 1).join(adder) + value).slice(-length)
    }

    const getTimeStrFromNumber = (time: number): string => {
        const minutes = Math.floor(time / 60)
        const seconds = Math.floor(time - minutes * 60)

        const timeStr = getTimeMinutesOrSecondsStr(minutes, "0", 2) + ":" + getTimeMinutesOrSecondsStr(seconds, "0", 2)
        return timeStr
    }

    const handleTimeUpdate = (
        event: React.SyntheticEvent<HTMLAudioElement> & { target: { currentTime: number; duration: number } }
    ) => {
        const currentTime = getTimeStrFromNumber(event.target.currentTime)
        setCurrentTimeStr(currentTime)

        if (!isMoving && lineRef.current && progressRef.current && thumbRef.current && audioRef.current) {
            const newValue = Math.max(0, Math.min(100, (event.target.currentTime / event.target.duration) * 100))
            const newValueStr = newValue + "%"
            progressRef.current.style.width = newValueStr
            thumbRef.current.style.left = newValueStr
        }
    }

    const handleLoadStart = () => {
        setIsLoading(true)
    }

    const handleCanPlay = (event: React.SyntheticEvent<HTMLAudioElement> & { target: { duration: number } }) => {
        setTotalTimeStr(getTimeStrFromNumber(event.target.duration))
        setIsLoading(false)
    }

    const toggleSettingsOpened = () => {
        setSettingsOpened(prev => !prev)
    }

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>, isFirst?: boolean) => {
            if (
                (isMoving || isFirst) &&
                lineRef.current &&
                progressRef.current &&
                thumbRef.current &&
                audioRef.current
            ) {
                e.preventDefault()

                let newValue =
                    ((e.clientX - lineRef.current.getBoundingClientRect().left) / lineRef.current.offsetWidth) * 100
                newValue = Math.max(0, Math.min(100, newValue))

                const newValueStr = newValue + "%"

                audioRef.current.currentTime = (newValue / 100) * audioRef.current.duration

                progressRef.current.style.width = newValueStr
                thumbRef.current.style.left = newValueStr
            }
        },
        [isMoving]
    )

    const handleLineMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            setMoving(true)
            handleMouseMove(e, true)
        },
        [handleMouseMove]
    )

    const handleRepeat = () => {
        if (lineRef.current && progressRef.current && thumbRef.current && audioRef.current) {
            const newValueStr = "0%"
            setCurrentTimeStr("00:00")

            progressRef.current.style.width = newValueStr
            thumbRef.current.style.left = newValueStr

            audioRef.current.currentTime = 0
            handlePlay()
        }
    }

    const handleDownload = () => {
        fetchFileVirtual(props.attrs.href, props.attrs.payload.fileName)
    }

    useEffect(() => {
        const onWindowMouseMove = (e: MouseEvent) => {
            if (isMoving) {
                e.preventDefault()

                handleMouseMove(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>)
            }
        }
        const onWindowMouseUp = () => {
            setMoving(false)
        }

        window.addEventListener("mousemove", onWindowMouseMove)
        window.addEventListener("mouseup", onWindowMouseUp)

        return () => {
            window.removeEventListener("mousemove", onWindowMouseMove)
            window.removeEventListener("mouseup", onWindowMouseUp)
        }
    }, [lineRef, isMoving, handleMouseMove])

    return (
        <>
            <div
                className={cn(
                    styles.audioPlayer,
                    props.primary && styles.audioPlayer_primary,
                    props.small && styles.audioPlayer_small,
                    props.className
                )}
                data-testid={`${dialogsTestIds.audioPlayer}-${audioItemId}`}
            >
                {isLoading && <Skeleton className={styles.audioPlayer__loader} />}
                <div
                    className={cn(
                        styles.audioPlayer__playBtnContainer,
                        props.small && styles.audioPlayer__playBtnContainer_small,
                        props.primary && styles.audioPlayer__playBtnContainer_primary
                    )}
                    onClick={isPlaying ? handlePause : handlePlay}
                    data-testid={`${dialogsTestIds.audioPlayerPlayButton}-${audioItemId}`}
                >
                    {isPlaying ? (
                        <PauseIcon
                            className={cn(
                                styles.audioPlayer__playIcon,
                                props.small && styles.audioPlayer__playIcon_small,
                                props.primary && styles.audioPlayer__playIcon_primary
                            )}
                        />
                    ) : (
                        <PlayIcon
                            className={cn(
                                styles.audioPlayer__playIcon,
                                props.small && styles.audioPlayer__playIcon_small,
                                props.primary && styles.audioPlayer__playIcon_primary
                            )}
                        />
                    )}
                </div>
                <div
                    className={cn(
                        styles.audioPlayer__duration,
                        props.primary && styles.audioPlayer__duration_primary,
                        props.small && styles.audioPlayer__duration_small
                    )}
                >
                    {`${currentTimeStr} / ${totalTimeStr}`}
                </div>
                <div
                    className={cn(
                        styles.audioPlayer__lineContainer,
                        props.small && styles.audioPlayer__lineContainer_small
                    )}
                    onMouseDown={handleLineMouseDown}
                    ref={lineRef}
                    data-testid={`${dialogsTestIds.audioPlayerTimeline}-${audioItemId}`}
                >
                    <div className={styles.audioPlayer__line}>
                        <div
                            className={cn(
                                styles.audioPlayer__thumb,
                                props.primary && styles.audioPlayer__thumb_primary
                            )}
                            ref={thumbRef}
                        ></div>
                        <div
                            className={cn(
                                styles.audioPlayer__progress,
                                props.primary && styles.audioPlayer__progress_primary
                            )}
                            ref={progressRef}
                        ></div>
                    </div>
                </div>
                <button
                    type="button"
                    className={cn(
                        styles.audioPlayer__controlBtn,
                        props.primary && styles.audioPlayer__controlBtn_primary,
                        props.small && styles.audioPlayer__controlBtn_small
                    )}
                    onClick={toggleMute}
                    data-testid={`${dialogsTestIds.audioPlayerVolumeToggle}-${audioItemId}`}
                >
                    <FontAwesomeIcon
                        icon={isMuted ? faVolumeMute : faVolumeUp}
                        size="lg"
                        className={cn(
                            styles.audioPlayer__volumeIcon,
                            props.primary && styles.audioPlayer__volumeIcon_primary,
                            props.small && styles.audioPlayer__volumeIcon_small
                        )}
                    />
                </button>
                <button
                    type="button"
                    className={cn(
                        `${styles.audioPlayer__controlBtn} ${styles.audioPlayer__cogBtn}`,
                        props.primary && styles.audioPlayer__cogBtn_primary,
                        props.small && styles.audioPlayer__controlBtn_small
                    )}
                    onClick={toggleSettingsOpened}
                    data-testid={`${dialogsTestIds.audioPlayerSettingsToggle}-${audioItemId}`}
                >
                    <FontAwesomeIcon
                        icon={faCog}
                        size="lg"
                        className={cn(
                            styles.audioPlayer__cogIcon,
                            props.primary && styles.audioPlayer__cogIcon_primary,
                            props.small && styles.audioPlayer__cogIcon_small
                        )}
                    />
                </button>
                {isSettingsOpened && (
                    <div className={cn(styles.audioPlayer__settingsContainer, props.settingsClassName)}>
                        <DownloadIcon
                            onClick={handleDownload}
                            data-testid={`${dialogsTestIds.audioPlayerDownloadButton}-${audioItemId}`}
                        />
                        <RepeatIcon
                            onClick={handleRepeat}
                            data-testid={`${dialogsTestIds.audioPlayerRepeatButton}-${audioItemId}`}
                        />
                    </div>
                )}
            </div>
            <audio
                controls
                ref={audioRef}
                onTimeUpdate={handleTimeUpdate}
                onLoadStart={handleLoadStart}
                onCanPlayThrough={handleCanPlay}
                onEnded={handleEnded}
                hidden
            >
                <source src={props.attrs.href} type={props.attrs.payload.fileType} />
                <div className={styles.audioPlayer__fallbackContainer}>
                    {`${t(`${tNamespace}.audio-format-warning`)} - `}
                    <a href={props.attrs.href}>{t(`${tNamespace}.audio-format-fallback-link`)}</a>
                </div>
            </audio>
        </>
    )
}
